import SharedUtils from "../components/shared/Common/utils";
import backendService from "../services/backend.service";
import { METADATA_KEY } from "./utils";
import { applyQuickLabelRollingToDateParams } from "./dateQuickRanges";
const hasFilter = (filterName) => {
    const value = localStorage.getItem(filterName);
    if (!value) return false;
    if (value.startsWith('[') && value.endsWith(']')) {
        try {
            const parsedArray = JSON.parse(value);
            return Array.isArray(parsedArray) && parsedArray.length > 0;
        } catch {
            return false;
        }
    }
    return value !== '';
};

export const convertToBytes = ({ value, size }) => {
  const sizeInLower = size.toLowerCase();

  const units = {
    b: 1,
    kb: 1024,
    mb: 1024 ** 2,
    gb: 1024 ** 3,
    tb: 1024 ** 4,
  };

  if (!units[sizeInLower]) {
    throw new Error(`Invalid size unit: ${size}`);
  }

  return Number(value) * units[sizeInLower];
}

/** Parse byte count from detail panel / URL (handles numbers, localized strings, N/A). */
export const parsePanelNumericByteValue = (value) => {
  if (value == null || value === '') return NaN;
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const s = String(value).replace(/,/g, '').trim();
  if (!s || s.toLowerCase() === 'n/a') return NaN;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : NaN;
};

export const getFilterDisplayStyle = (filters) => {
    if(!filters || filters.length === 0) return "none";
    let isEventFilters = false;
    filters.forEach(element => {
        if(element.includes('event_')){
            isEventFilters = true;
        }
    });
    
    if (isEventFilters) {
        const eventVerdictFilter = JSON.parse(localStorage.getItem('event_verdictFilter'));
        const hasDefaultVerdictFilter = Array.isArray(eventVerdictFilter) && 
                                        eventVerdictFilter.length === 2 && 
                                        eventVerdictFilter.includes("Blocked") && 
                                        eventVerdictFilter.includes("Detected");
    
        if (!hasDefaultVerdictFilter) {
            return "block";
        }
        
        const otherFiltersActive = filters
            .filter(filter => filter !== 'event_verdictFilter' || !hasDefaultVerdictFilter)
            .some(filter => {
                const value = localStorage.getItem(filter);
                return value !== null && value !== '[]' && value !== '""';
            });
            
        return otherFiltersActive ? "block" : "none";
    }
    const otherFiltersActive = filters.some(filter => {
        const value = localStorage.getItem(filter);
        return value !== null && value !== '[]' && value !== '""';
    });
    
    return otherFiltersActive ? "block" : "none";
};

export const fetchAndSetMeta = async ({
    component,
    storageKey,
    preCallback = null,
    postCallback = null
}) => {
    try {
        if (preCallback) {
            await preCallback();
        }
        const metaData = JSON.parse(localStorage.getItem(METADATA_KEY)) || {};
            if (!metaData?.genesis) {
                throw new Error("Invalid meta data response");
            }
            if (component._mounted === false) return;
            await new Promise(resolve => {
                component.setState({
                    genesis: new Date(metaData.genesis),
                    startDate: new Date(metaData.genesis),
                    endDate: new Date()
                }, () => {
                    if (postCallback) {
                        postCallback();
                    }
                    resolve();
                });
            });
    
            return [metaData];

    } catch (err) {
        if(err.message !== 'Invalid meta data response'){
            console.error(err);
        }
    }
};
export const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
  
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);
  
    if (minutes < 1) {
      return 'just now';
    } else if (minutes < 60) {
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (hours < 24) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (days < 7) {
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else if (weeks < 5 && weeks > 0) {
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    } else if (months < 12 && months > 0) {
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    } else {
      return `${years} ${years === 1 ? 'year' : 'years'} ago`;
    }
  };

  const handleEmptyFilterItem = (prefix, filterName) => {
    if((prefix === "eventV2_" && filterName === "traffic type") || (prefix === "eventV2_" && filterName === "traffic_type") || (prefix === "trafficV2_" && filterName === "traffic type") || (prefix === "trafficV2_" && filterName === "traffic_type")) return `Records Without ${sanitizeText(filterName)}`;
    if (prefix === "trafficV2_" || prefix === "auditV2_") return `Logs Without ${sanitizeText(filterName)}`;
    else return `Events Without ${sanitizeText(filterName)}`
  }

export const transformApiToFilters = (apiResponse, prefix = "") => {
    const result = [];
    const keyLower = (k) => k.toLowerCase();

    Object.keys(apiResponse).forEach((key) => {
      if (!Array.isArray(apiResponse[key]) || apiResponse[key].length === 0) return;

      const baseId = keyLower(key);
      const makeSubFilters = () => {
        const mapped = apiResponse[key]
          .map((item) => {
            if (typeof item !== "string" && typeof item !== "number") return null;
            return {
              fid: item,
              title: item === "*" ? "Global" : item === "" ? handleEmptyFilterItem(prefix, key) : item,
              checked: false
            };
          })
          .filter((f) => f !== null);
        return mapped.sort((a, b) => {
          if (a.fid === "" && b.fid !== "") return -1;
          if (a.fid !== "" && b.fid === "") return 1;
          return 0;
        });
      };

      result.push({
        id: `${baseId}_include`,
        baseId,
        title: key,
        filterType: 'array',
        isActive: false,
        isOpen: false,
        mode: 'include',
        localStorageKey: `${prefix}${baseId}`,
        filters: makeSubFilters()
      });
      result.push({
        id: `${baseId}_exclude`,
        baseId,
        title: key,
        filterType: 'array',
        isActive: false,
        isOpen: false,
        mode: 'exclude',
        localStorageKey: `${prefix}${baseId}_excluded`,
        filters: makeSubFilters()
      });
    });

    return result;
};

export const getFilterDataTestIdKey = (filter) =>
  filter?.dataTestIdKey || filter?.localStorageKey || '';

export const getFilterDataTestIdBaseId = (filter) =>
  filter?.dataTestIdBaseId || filter?.baseId || '';

  export const getFilterParams = (filtersList) => {
    const params = {};

    // Always include date_picker range when present (e.g. Contractors) so payload gets updated start/end date
    filtersList.forEach((filterGroup) => {
      if (filterGroup.filterType === 'date_picker' && filterGroup.id === 'date_picker' && filterGroup.value) {
        const start_date = filterGroup.value?.start_date;
        const end_date = filterGroup.value?.end_date;
        if (start_date) params.start_date = start_date;
        if (end_date) params.end_date = end_date;
      }
    });

    filtersList.forEach((filterGroup) => {
      if (filterGroup.isActive) {
        if(filterGroup.filterType === 'date'){
          const date = filterGroup.value;
          if(date){
            if(filterGroup.id === "start_date"){
              params.start_date = date;
            } 
            if(filterGroup.id === "end_date"){
              params.end_date = date;
            }
          }
        }
        if(filterGroup.filterType === 'date_picker' && (filterGroup.id === 'date_picker' || filterGroup.baseId === 'date_picker')){
          const isExclude = filterGroup.mode === 'exclude';
          const prefix = isExclude ? 'exclude_' : '';
          const start_date = filterGroup.value?.start_date;
          const end_date = filterGroup.value?.end_date;
          if(start_date){
            params[prefix + 'start_date'] = start_date;
          }
          if(end_date){
            params[prefix + 'end_date'] = end_date;
          }
        }
        if(filterGroup.filterType === 'date_picker' && filterGroup.id === 'last_seen'){
          const start_date = filterGroup.value?.start_date;
          const end_date = filterGroup.value?.end_date;
          params.date_filter_by = ['lastseen'];
          if(start_date){
              params.start_date = start_date;
          }
          if(end_date){
              params.end_date = end_date;
          }
        }
        if(filterGroup.filterType === 'date_picker' && filterGroup.id === 'activation'){
          const start_date = filterGroup.value?.start_date;
          const end_date = filterGroup.value?.end_date;
          params.date_filter_by = ['activation'];
          if(start_date){
              params.start_date = start_date;
          }
          if(end_date){
              params.end_date = end_date;
          }
        }
        if (filterGroup.filterType === 'array' || filterGroup.filterType === 'select') {
          const baseId = filterGroup.baseId || filterGroup.id.replace(/_include$|_exclude$/, '') || filterGroup.id;
          const isExclude = filterGroup.mode === 'exclude' || (filterGroup.id && filterGroup.id.endsWith('_exclude'));
          const checkedFilters = filterGroup.filters
            .filter((f) => f.checked)
            .map((f) => (f.title === "Global" ? "*" : f.fid));

          if (checkedFilters.length > 0) {
            if (isExclude) {
              params[`exclude_${baseId}`] = checkedFilters;
            } else {
              params[baseId] = checkedFilters;
            }
          }
        }
        if(filterGroup.filterType === 'freetext'){
          const baseId = filterGroup.baseId || filterGroup.id.replace(/_include$|_exclude$/, '') || filterGroup.id;
          const isExclude = filterGroup.mode === 'exclude' || (filterGroup.id && filterGroup.id.endsWith('_exclude'));
          const checkedFilters = (filterGroup.filters || [])
            .filter((f) => f.checked)
            .map((f) => f.fid);
          if (checkedFilters.length > 0) {
            if (isExclude) {
              params[`exclude_${baseId}`] = checkedFilters;
            } else {
              params[baseId] = checkedFilters;
            }
          }
        }
        if(filterGroup.filterType === 'string'){
          const text = filterGroup.value;
          if(text){
            const baseId = filterGroup.baseId || filterGroup.id;
            const isExclude = filterGroup.mode === 'exclude';
            if (isExclude) {
              params[`exclude_${baseId}`] = text;
            } else {
              params[baseId] = text;
            }
          }
        }
        if(filterGroup.filterType === 'customBytes'){
          const data = filterGroup.value;
          if(data){
            const bytes = convertToBytes(data);
            if(filterGroup.id === 'bytes_sent_tx'){
              if(data.condition === 'gt'){
                params['transmit_bytes_min'] = bytes;
              }
              if(data.condition === 'ls'){
                params['transmit_bytes_max'] = bytes;
              }
              if(data.condition === 'eq'){
                params['transmit_bytes_min'] = bytes;
                params['transmit_bytes_max'] = bytes;
              }
            }
            if(filterGroup.id === 'bytes_received_rx'){
              if(data.condition === 'gt'){
                params['receive_bytes_min'] = bytes;
              }
              if(data.condition === 'ls'){
                params['receive_bytes_max'] = bytes;
              }
              if(data.condition === 'eq'){
                params['receive_bytes_min'] = bytes;
                params['receive_bytes_max'] = bytes;
              }
            }
            if(filterGroup.id === 'size_in_bytes'){
              if(data.condition === 'gt'){
                params['min_size_in_bytes'] = bytes;
              }
              if(data.condition === 'ls'){
                params['max_size_in_bytes'] = bytes;
              }
              if(data.condition === 'eq'){
                params['min_size_in_bytes'] = bytes;
                params['max_size_in_bytes'] = bytes;
              }
            }
          }
        }
        if(filterGroup.filterType === 'conditional'){
          const data = filterGroup.value;
          if(data){
            if(filterGroup.id === 'active_users'){
              if(data.condition === 'gt'){
                params['min_user_count'] = data?.value;
              }
              if(data.condition === 'ls'){
                params['max_user_count'] = data?.value;
              }
              if(data.condition === 'eq'){
                params['min_user_count'] = data?.value;
                params['max_user_count'] = data?.value;
              }
            }
            if(filterGroup.id === 'store_rating'){
              if(data.condition === 'gt'){
                params['min_star_count'] = data?.value;
              }
              if(data.condition === 'ls'){
                params['max_star_count'] = data?.value;
              }
              if(data.condition === 'eq'){
                params['min_star_count'] = data?.value;
                params['max_star_count'] = data?.value;
              }
            }
            if(filterGroup.id === 'votes'){
              if(data.condition === 'gt'){
                params['min_vote_count'] = data?.value;
              }
              if(data.condition === 'ls'){
                params['max_vote_count'] = data?.value;
              }
              if(data.condition === 'eq'){
                params['min_vote_count'] = data?.value;
                params['max_vote_count'] = data?.value;
              }
            }
            if(filterGroup.id === 'req_duration_time'){
              if(data.condition === 'gt'){
                params['req_duration_time_min'] = data?.value;
              }
              if(data.condition === 'ls'){
                params['req_duration_time_max'] = data?.value;
              }
              if(data.condition === 'eq'){
                params['req_duration_time_min'] = data?.value;
                params['req_duration_time_max'] = data?.value;
              }
            }
          }
        }
      }
    });

    return applyQuickLabelRollingToDateParams(filtersList, params);
  };
  
  export const SAAS_V2_DATE_PICKER_KEY = 'saasV2_date_picker';

  export const getStorageItem = (key) => {
    const item = localStorage.getItem(key);
    if (!item) return [];
    
    try {
      return JSON.parse(item);
    } catch (e) {
      console.error(`Error parsing ${key} from localStorage:`, e);
      return [];
    }
  };

  export const isDefaultDateRange = ({ startDate, endDate, genesisDate, todayDate = new Date() }) => {
    // Convert date to YYYY-MM-DD format for comparison
    const formatDate = (date) => {
      try {
        if (!date) return '';
        return new Date(date).toISOString().split('T')[0];
      } catch (error) {
        console.error("Error formatting date:", error);
        return '';
      }
    };
    
    // Format all dates
    const startFormatted = formatDate(startDate);
    const endFormatted = formatDate(endDate);
    const genesisFormatted = formatDate(genesisDate);
    const todayFormatted = formatDate(todayDate);
    
    // Check only start date
    if (startFormatted && genesisFormatted && !endFormatted) {
      return startFormatted === genesisFormatted;
    }
    
    // Check only end date
    if (endFormatted && !startFormatted) {
      return endFormatted === todayFormatted;
    }
    
    // Check both dates
    if (startFormatted && endFormatted && genesisFormatted) {
      return startFormatted === genesisFormatted && endFormatted === todayFormatted;
    }
    
    // If we can't make a comparison, return false
    return false;
  };

  /**
   * Returns only those default filter keys that are not already stored in localStorage.
   * Use this to apply default filters only for keys the user hasn't saved yet.
   */
  export const getDefaultFiltersNotInStorage = (filters, defaultFilters) => {
    if (!filters || !defaultFilters) return {};
    const result = {};
    for (const key of Object.keys(defaultFilters)) {
      const item = filters.find(f => f.id === key);
      if (!item) {
        result[key] = defaultFilters[key];
        continue;
      }
      const stored = item.localStorageKey ? localStorage.getItem(item.localStorageKey) : null;
      const hasStoredValue = stored != null && stored !== '' && stored !== '[]' && stored !== 'null' && stored !== '""';
      if (!hasStoredValue) {
        result[key] = defaultFilters[key];
      }
    }
    return result;
  };

  export const applyDefaultFilters = (filters, defaultFilters, updateLocalStorage = true) => {
    if (!filters || !defaultFilters) {
      return filters;
    }
  
    return filters.map(item => {
      if (defaultFilters[item.id]) {
        if (updateLocalStorage && item.localStorageKey) {
          localStorage.setItem(item.localStorageKey, JSON.stringify(defaultFilters[item.id]));
        }
  
        if (item.filterType === 'array' && Array.isArray(item.filters)) {
          let isActive = false;
          item.filters.map(f => {
            if(defaultFilters[item.id].includes(f.fid) && JSON.parse(localStorage.getItem(item.localStorageKey))){
              isActive = true;
            }
          });
          return {
            ...item,
            isActive: isActive,
            isOpen: false,
            filters: item.filters.map(f => {
              return ({
              ...f,
              checked: (defaultFilters[item.id].includes(f.fid) && JSON.parse(localStorage.getItem(item.localStorageKey))) ? true : false
             })})
          };
        }

        if (item.filterType === 'select' && Array.isArray(item.filters)) {
          return {
            ...item,
            isActive: true,
            isOpen: false,
            filters: item.filters.map(f => ({
              ...f,
              checked: defaultFilters[item.id][0] === f.fid
            }))
          };
        }
        
        if ((item.filterType === 'date' || item.filterType === 'string') && 
            typeof defaultFilters[item.id] !== 'object') {
          return {
            ...item,
            isActive: true,
            isOpen: false,
            value: defaultFilters[item.id]
          };
        }
        if ((item.filterType === 'customBytes') && 
            typeof defaultFilters[item.id] !== 'object') {
          return {
            ...item,
            isActive: true,
            isOpen: false,
            value: defaultFilters[item.id]
          };
        }
        if ((item.filterType === 'date_picker') && 
            defaultFilters[item.id] && typeof defaultFilters[item.id] === 'object') {
          return {
            ...item,
            isActive: true,
            isOpen: false,
            value: {
              start_date: defaultFilters[item.id]?.start_date,
              end_date: defaultFilters[item.id]?.end_date,
              quickLabel: defaultFilters[item.id]?.quickLabel,
            }
          };
        }
      }
      
      return item;
    });
  };
  export const sanitizeText = (inputText) => {
    if (!inputText) return "";
    if(typeof inputText !== 'string') return "";
    const lower = inputText.toLowerCase();
    if(lower === "url") return "URL";
    if(lower === "ip") return "IP";
    if(lower === "tls_protocol_ver" || lower === "tls protocol ver") return "TLS Version";
    if(lower === "httpversion" || lower === "http_version") return "HTTP Version";
    if(lower === "receive_bytes") return "Bytes Received";
    if(lower === "transmit_bytes") return "Bytes Sent";
    if (lower === "username" || lower === "user_name") return "User";
    if (lower === "useragent" || lower === "user_agent") return "User Agent";
    if (lower === "event_id" || lower === "event id") return "Event ID";
    if (lower === "dlp_categories" || lower === "dlp category") return "DLP Category";
    if (lower === "datebody" || lower === "date_body") return "Date";
    if (lower === "policy id" || lower === "policy_id") return "Policy ID";
    return inputText
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => {
        const wl = word.toLowerCase();
        if (wl === 'ip') return 'IP';
        if (wl === 'id') return 'ID';
        if (wl === 'tls') return 'TLS';
        if (wl === 'url') return 'URL';
        if (wl === 'http') return 'HTTP';
        if (wl === 'uuid') return 'UUID';
        if (wl === 'siem') return 'SIEM';
        if (wl === 'dns') return 'DNS';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };



 /**
 * Creates a UTC timestamp for the start of the user's selected calendar day (00:00:00.000Z).
 * Uses local date parts so filter pickers that store local datetimes (e.g. 2026-07-12T00:00:00)
 * keep the same calendar day in the API payload regardless of browser timezone.
 * @param {Date|string} dateInput - Date object or date string
 * @returns {Date} Date object set to start of day in UTC for the local calendar date
 */
export function createStartOfDayUTC(dateInput) {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

    return new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0, 0, 0, 0
    ));
  }
  
  /**
   * Creates a UTC timestamp for the end of the user's selected calendar day (23:59:59.999Z).
   * Uses local date parts so filter pickers that store local datetimes keep the same calendar day.
   * @param {Date|string} dateInput - Date object or date string
   * @returns {Date} Date object set to end of day in UTC for the local calendar date
   */
export function createEndOfDayUTC(dateInput) {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

    return new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23, 59, 59, 999
    ));
  }

  /**
   * Converts a date to ISO string format preserving local time (not UTC)
   * @param {Date|string} dateInput - Date object or date string
   * @returns {string} ISO-like string in local timezone format (YYYY-MM-DDTHH:mm:ss)
   */
  export function toLocalISOString(dateInput) {
    if (!dateInput) {
      return new Date().toISOString();
    }
    
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return new Date().toISOString();
    }
    
    // Format date in local timezone (not UTC)
    const pad = (n) => n < 10 ? `0${n}` : n;
    const padMilliseconds = (n) => String(n).padStart(3, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const milliseconds = padMilliseconds(date.getMilliseconds());
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
  }

  export function renameKeys(obj, keyMap) {
    const newObj = {};
    const normalizedKeyMap = {};
    for (const key in keyMap) {
      normalizedKeyMap[key.toLowerCase()] = keyMap[key];
    }

    for (const key in obj) {
      const lookupKey = key.toLowerCase();
      const newKey = normalizedKeyMap[lookupKey] || key;
      newObj[newKey] = obj[key];
    }

    return newObj;
  }

  export function normalizePermissions(obj) {
  if (!obj) return obj;

  const newData = obj.map(item => {
    if (Array.isArray(item.permissions)) {
      let perms = [];

      item.permissions.forEach(p => {
        if (typeof p === "string") {
          perms.push(p);
        } else if (typeof p === "object" && p !== null) {
          Object.entries(p).forEach(([key, values]) => {
            if (Array.isArray(values)) {
              values.forEach(val => perms.push(`${key}: ${val}`));
            }
          });
        }
      });

      // remove duplicates
      perms = [...new Set(perms)];

      return { ...item, permissions: perms };
    }
    return item;
  });

  return newData || [];
}

const readablePolicyNames = {
  urlfiltering: "URL Filtering",
  filescan: "File Scan",
  browserprotection: "Browser Protection",
  browserextension: "Browser Extension"
};

export function formatPolicyRuleString(str = "") {
  const parts = str.split(" ");
  const firstWord = parts[0];
  const key = firstWord.toLowerCase();

  if (readablePolicyNames[key]) {
    parts[0] = readablePolicyNames[key];
  }

  return parts.join(" ");
}

export function buildQuery(paramsArray, existingSearch = window.location.search) {
  const search = new URLSearchParams(existingSearch);

  paramsArray.forEach(({ key, value }) => {
    if (key && value !== undefined && value !== null) {
      // If value is an array, join with || (double pipe) delimiter
      const stringValue = Array.isArray(value) ? value.join('||') : String(value);
      const existingValues = search.getAll(key);

      // append only if same key=value does NOT already exist
      if (!existingValues.includes(stringValue)) {
        search.append(key, stringValue);
      }
    }
  });

  const query = search.toString();
  return query ? `?${query}` : "";
}

